import React, { useEffect, useState } from 'react';
import './AreasAnalytics.scss';
import api from '../../../utils/api';
import Loader from '../../../components/Loader';
import { usePermissions } from '../../../hooks/usePermissions';

const AreasAnalytics = () => {
  const { hasPermission } = usePermissions();
  const [mostUsedAreas, setMostUsedAreas] = useState([]);
  const [mostRequestedAreas, setMostRequestedAreas] = useState([]);
  const [activeTab, setActiveTab] = useState('used');
  const [loading, setLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalRequests, setTotalRequests] = useState(0);

  useEffect(() => {
    fetchAreasAndGovernorates();
  }, []);

  const fetchAreasAndGovernorates = async () => {
    try {
      setLoading(true);
      const response = await api.get("/admin/governorates-stats");
      
      if (response.data.status === 'success') {
        const { allUnSupportedGovernorates, bookingAreaGovCount } = response.data.data;
        
        // Transform bookingAreaGovCount to mostUsedAreas format
        const usedAreas = bookingAreaGovCount.map((item, index) => {
          const percentage = calculatePercentage(item.count, bookingAreaGovCount);
          const trend = determineTrend(item.count, bookingAreaGovCount);
          
          return {
            id: index + 1,
            name: item.governorateName.nameAr,
            area: item.areaName.nameAr,
            users: item.count,
            percentage: percentage,
            trend: trend
          };
        });
        
        // Sort by count (users) in descending order
        usedAreas.sort((a, b) => b.users - a.users);
        
        // Transform allUnSupportedGovernorates to mostRequestedAreas format
        const requestedAreas = allUnSupportedGovernorates.map((item, index) => {
          const priority = determinePriority(item.expansionRequestsCount, allUnSupportedGovernorates);
          
          return {
            id: index + 1,
            name: item.name.ar,
            area: item.areaName.nameAr,
            requests: item.expansionRequestsCount,
            priority: priority
          };
        });
        
        // Sort by requests in descending order
        requestedAreas.sort((a, b) => b.requests - a.requests);
        
        setMostUsedAreas(usedAreas);
        setMostRequestedAreas(requestedAreas);
        
        // Calculate totals
        const totalUsersCount = usedAreas.reduce((sum, area) => sum + area.users, 0);
        const totalRequestsCount = requestedAreas.reduce((sum, area) => sum + area.requests, 0);
        
        setTotalUsers(totalUsersCount);
        setTotalRequests(totalRequestsCount);
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching areas and governorates:", error);
      setLoading(false);
    }
  };

  // Helper function to calculate percentage
  const calculatePercentage = (count, allAreas) => {
    const total = allAreas.reduce((sum, item) => sum + item.count, 0);
    return total > 0 ? Math.round((count / total) * 100) : 0;
  };

  // Dynamic trend calculation based on actual data distribution
  const determineTrend = (currentCount, allAreas) => {
    if (allAreas.length === 0) return 'stable';
    
    // Calculate average and standard deviation
    const counts = allAreas.map(item => item.count);
    const average = counts.reduce((sum, count) => sum + count, 0) / counts.length;
    
    // Calculate standard deviation
    const squaredDiffs = counts.map(count => Math.pow(count - average, 2));
    const variance = squaredDiffs.reduce((sum, diff) => sum + diff, 0) / counts.length;
    const stdDeviation = Math.sqrt(variance);
    
    // Determine trend based on standard deviations from mean
    if (currentCount >= average + (stdDeviation * 0.5)) {
      return 'up'; // Above average + half std deviation
    } else if (currentCount <= average - (stdDeviation * 0.5)) {
      return 'down'; // Below average - half std deviation
    } else {
      return 'stable'; // Within average range
    }
  };

  // Dynamic priority calculation based on percentile distribution
  const determinePriority = (requestCount, allRequests) => {
    if (allRequests.length === 0) return 'low';
    
    // Get all request counts and sort them in descending order
    const requestCounts = allRequests.map(item => item.expansionRequestsCount).sort((a, b) => b - a);
    
    // Calculate percentiles
    const total = requestCounts.length;
    const highThresholdIndex = Math.ceil(total * 0.33); // Top 33%
    const mediumThresholdIndex = Math.ceil(total * 0.67); // Top 67%
    
    const highThreshold = requestCounts[highThresholdIndex - 1] || 0;
    const mediumThreshold = requestCounts[mediumThresholdIndex - 1] || 0;
    
    // Determine priority based on percentile
    if (requestCount >= highThreshold) {
      return 'high';
    } else if (requestCount >= mediumThreshold) {
      return 'medium';
    } else {
      return 'low';
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === 'up') return '↗';
    if (trend === 'down') return '↘';
    return '→';
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      high: { text: 'أولوية عالية', class: 'high' },
      medium: { text: 'أولوية متوسطة', class: 'medium' },
      low: { text: 'أولوية منخفضة', class: 'low' }
    };
    return badges[priority];
  };

  if (loading) {
    return (
<Loader />
    );
  }

  return (
    <div className="container">
      <div className="analytics-header">
        <div className="header-content">
          <h1>تحليلات المناطق الجغرافية</h1>
          <p>متابعة أداء المناطق والمحافظات الأكثر استخداماً وطلباً</p>
        </div>
        
        <div className="summary-cards">
          <div className="summary-card">
            <div className="card-icon users">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="card-info">
              <div className="card-number">{totalUsers.toLocaleString()}</div>
              <div className="card-label">إجمالي المستخدمين</div>
            </div>
          </div>
          
          <div className="summary-card">
            <div className="card-icon requests">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2"/>
                <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <div className="card-info">
              <div className="card-number">{totalRequests.toLocaleString()}</div>
              <div className="card-label">طلبات التوسع</div>
            </div>
          </div>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          {hasPermission(136) &&           <button 
            className={`tab ${activeTab === 'used' ? 'active' : ''}`}
            onClick={() => setActiveTab('used')}
          >
            <span className="tab-icon">📊</span>
            المناطق الأكثر استخداماً
          </button>}

          {hasPermission(137) &&          <button 
            className={`tab ${activeTab === 'requested' ? 'active' : ''}`}
            onClick={() => setActiveTab('requested')}
          >
            <span className="tab-icon">📍</span>
            المناطق المطلوب التوسع فيها
          </button> }


        </div>
      </div>

      <div className="content-section">
        {activeTab === 'used' ? (
          <div className="used-areas">
            <div className="section-header">
              <h2>المحافظات الأكثر استخداماً للتطبيق</h2>
              <span className="subtitle">مرتبة حسب عدد المستخدمين النشطين</span>
            </div>
            
            {mostUsedAreas.length === 0 ? (
              <div className="no-data">لا توجد بيانات متاحة</div>
            ) : (
              <div className="areas-list">
                {mostUsedAreas.map((area, index) => (
                  <div key={area.id} className="area-item">
                    <div className="rank-badge">{index + 1}</div>
                    
                    <div className="area-details">
                      <div className="area-name-group">
                        <h3>{area.name}</h3>
                        <span className="area-region">{area.area}</span>
                      </div>
                      
                      <div className="area-stats">
                        <div className="stat-group">
                          <span className="stat-number">{area.users.toLocaleString()}</span>
                          <span className="stat-label">مستخدم نشط</span>
                        </div>
                        
                        <div className="progress-container">
                          <div className="progress-bar">
                            <div 
                              className="progress-fill" 
                              style={{ width: `${area.percentage}%` }}
                            ></div>
                          </div>
                          <span className="percentage">{area.percentage}%</span>
                        </div>
                        
                        <div className={`trend trend-${area.trend}`}>
                          <span className="trend-icon">{getTrendIcon(area.trend)}</span>
                          <span className="trend-label">
                            {area.trend === 'up' ? 'في تزايد' : area.trend === 'down' ? 'في تناقص' : 'مستقر'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="requested-areas">
            <div className="section-header">
              <h2>المحافظات المطلوب التوسع فيها</h2>
              <span className="subtitle">طلبات المستخدمين لتفعيل الخدمات في مناطقهم</span>
            </div>
            
            {mostRequestedAreas.length === 0 ? (
              <div className="no-data">لا توجد طلبات توسع حالياً</div>
            ) : (
              <div className="areas-list">
                {mostRequestedAreas.map((area, index) => (
                  <div key={area.id} className="area-item requested">
                    <div className="rank-badge secondary">{index + 1}</div>
                    
                    <div className="area-details">
                      <div className="area-name-group">
                        <h3>{area.name}</h3>
                        <span className="area-region">{area.area}</span>
                      </div>
                      
                      <div className="area-stats">
                        <div className="stat-group">
                          <span className="stat-number">{area.requests.toLocaleString()}</span>
                          <span className="stat-label">طلب توسع</span>
                        </div>
                        
                        <div className={`priority-badge ${getPriorityBadge(area.priority).class}`}>
                          {getPriorityBadge(area.priority).text}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AreasAnalytics;